import React, { useState, useEffect, useRef } from 'react';
import { analyticsService } from '../api/analyticsService';
import '../components/Common/Common.css';
import './Analytics.css';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

function Analytics() {
  const [dateRange, setDateRange] = useState('7');
  const [customDateFrom, setCustomDateFrom] = useState('');
  const [customDateTo, setCustomDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Analytics data states
  const [overview, setOverview] = useState(null);
  const [visits, setVisits] = useState(null);
  const [events, setEvents] = useState(null);
  const [orderStats, setOrderStats] = useState(null);
  const [salesStats, setSalesStats] = useState(null);
  const [cartSummary, setCartSummary] = useState(null);
  const [abandonedCarts, setAbandonedCarts] = useState(null);

  // Fetch all analytics data
  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Build date parameters
      const params = {};
      if (dateRange === 'custom') {
        if (customDateFrom) params.date_from = customDateFrom;
        if (customDateTo) params.date_to = customDateTo;
      } else {
        const daysAgo = parseInt(dateRange);
        const fromDate = new Date();
        fromDate.setDate(fromDate.getDate() - daysAgo);
        params.date_from = fromDate.toISOString().split('T')[0];
      }

      // Fetch all data in parallel
      const [
        overviewData,
        visitsData,
        eventsData,
        orderStatsData,
        salesStatsData,
        cartSummaryData,
        abandonedCartsData,
      ] = await Promise.all([
        analyticsService.getOverview(params).catch(err => {
          console.error('Error fetching overview:', err);
          return null;
        }),
        analyticsService.getVisits(params).catch(err => {
          console.error('Error fetching visits:', err);
          return null;
        }),
        analyticsService.getEvents(params).catch(err => {
          console.error('Error fetching events:', err);
          return null;
        }),
        analyticsService.getOrderStatistics(params).catch(err => {
          console.error('Error fetching order stats:', err);
          return null;
        }),
        analyticsService.getSalesStatistics(params).catch(err => {
          console.error('Error fetching sales stats:', err);
          return null;
        }),
        analyticsService.getCartSummary(params).catch(err => {
          console.error('Error fetching cart summary:', err);
          return null;
        }),
        analyticsService.getAbandonedCarts({ ...params, per_page: 10 }).catch(err => {
          console.error('Error fetching abandoned carts:', err);
          return null;
        }),
      ]);

      setOverview(overviewData);
      setVisits(visitsData);
      setEvents(eventsData);
      setOrderStats(orderStatsData);
      setSalesStats(salesStatsData);
      setCartSummary(cartSummaryData);
      setAbandonedCarts(abandonedCartsData);
    } catch (error) {
      console.error('Error fetching analytics:', error);
      setError('Failed to load analytics data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [dateRange, customDateFrom, customDateTo]);

  // Format number with commas
  const formatNumber = (num) => {
    if (!num && num !== 0) return '0';
    return num.toLocaleString();
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '0.00 DH';
    return `${parseFloat(amount).toFixed(2)} DH`;
  };

  // Format percentage
  const formatPercent = (value) => {
    if (!value && value !== 0) return '0%';
    return `${parseFloat(value).toFixed(2)}%`;
  };

  if (loading) {
    return <div className="loading">Loading analytics dashboard...</div>;
  }

  if (error) {
    return (
      <div className="error-message">
        <p>{error}</p>
        <button onClick={fetchAnalytics} className="btn-primary">Retry</button>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="page-header">
        <h1>Analytics Dashboard</h1>
        <div className="header-actions">
          <button onClick={fetchAnalytics} className="btn-secondary">
            Refresh Data
          </button>
        </div>
      </div>

      {/* Date Range Picker */}
      <div className="date-range-picker card">
        <div className="date-range-buttons">
          <button
            className={`date-btn ${dateRange === '7' ? 'active' : ''}`}
            onClick={() => setDateRange('7')}
          >
            Last 7 Days
          </button>
          <button
            className={`date-btn ${dateRange === '30' ? 'active' : ''}`}
            onClick={() => setDateRange('30')}
          >
            Last 30 Days
          </button>
          <button
            className={`date-btn ${dateRange === '90' ? 'active' : ''}`}
            onClick={() => setDateRange('90')}
          >
            Last 90 Days
          </button>
          <button
            className={`date-btn ${dateRange === 'custom' ? 'active' : ''}`}
            onClick={() => setDateRange('custom')}
          >
            Custom Range
          </button>
        </div>

        {dateRange === 'custom' && (
          <div className="custom-date-inputs">
            <div>
              <label>From:</label>
              <input
                type="date"
                value={customDateFrom}
                onChange={(e) => setCustomDateFrom(e.target.value)}
              />
            </div>
            <div>
              <label>To:</label>
              <input
                type="date"
                value={customDateTo}
                onChange={(e) => setCustomDateTo(e.target.value)}
              />
            </div>
          </div>
        )}
      </div>

      {/* Overview Metrics */}
      <div className="metrics-grid">
        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: '#3182ce' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
              <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            </svg>
          </div>
          <div className="metric-info">
            <div className="metric-label">Total Visits</div>
            <div className="metric-value">{formatNumber(overview?.total_visits || 0)}</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: '#38a169' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
              <circle cx="8.5" cy="7" r="4" />
              <path d="M20 8v6M23 11h-6" />
            </svg>
          </div>
          <div className="metric-info">
            <div className="metric-label">Unique Visitors</div>
            <div className="metric-value">{formatNumber(overview?.unique_visitors || 0)}</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: '#e53e3e' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
          </div>
          <div className="metric-info">
            <div className="metric-label">Total Events</div>
            <div className="metric-value">{formatNumber(overview?.total_events || 0)}</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: '#d69e2e' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
          <div className="metric-info">
            <div className="metric-label">Conversion Rate</div>
            <div className="metric-value">{formatPercent(overview?.conversion_rate || 0)}</div>
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-icon" style={{ backgroundColor: '#805ad5' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="metric-info">
            <div className="metric-label">Total Revenue</div>
            <div className="metric-value">{formatCurrency(salesStats?.total_revenue || overview?.total_revenue || 0)}</div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="charts-row">
        {/* Daily Visits Chart */}
        <div className="card chart-card">
          <div className="card-header">
            <h2>Daily Visits Trend</h2>
          </div>
          <div className="card-body">
            {visits && visits.daily_breakdown && visits.daily_breakdown.length > 0 ? (
              <Line
                data={{
                  labels: visits.daily_breakdown.map(day => day.date),
                  datasets: [
                    {
                      label: 'Visits',
                      data: visits.daily_breakdown.map(day => day.visits),
                      fill: true,
                      borderColor: '#3182ce',
                      backgroundColor: 'rgba(49, 130, 206, 0.1)',
                      tension: 0.4,
                      pointRadius: 4,
                      pointHoverRadius: 6,
                      pointBackgroundColor: '#3182ce',
                      pointBorderColor: '#fff',
                      pointBorderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 14,
                      },
                      bodyFont: {
                        size: 13,
                      },
                      callbacks: {
                        label: function(context) {
                          return 'Visits: ' + context.parsed.y.toLocaleString();
                        }
                      }
                    },
                  },
                  scales: {
                    x: {
                      grid: {
                        display: false,
                      },
                      ticks: {
                        font: {
                          size: 11,
                        },
                      },
                    },
                    y: {
                      beginAtZero: true,
                      grid: {
                        color: 'rgba(0, 0, 0, 0.05)',
                      },
                      ticks: {
                        font: {
                          size: 11,
                        },
                        callback: function(value) {
                          return value.toLocaleString();
                        }
                      },
                    },
                  },
                }}
                height={300}
              />
            ) : (
              <div className="no-data">No visits data available</div>
            )}
          </div>
        </div>

        {/* Device Breakdown */}
        <div className="card chart-card">
          <div className="card-header">
            <h2>Device Breakdown</h2>
          </div>
          <div className="card-body">
            {visits && visits.device_breakdown ? (
              <Doughnut
                data={{
                  labels: Object.keys(visits.device_breakdown),
                  datasets: [
                    {
                      data: Object.values(visits.device_breakdown),
                      backgroundColor: [
                        'rgba(49, 130, 206, 0.8)',
                        'rgba(56, 161, 105, 0.8)',
                        'rgba(128, 90, 213, 0.8)',
                        'rgba(237, 137, 54, 0.8)',
                        'rgba(229, 62, 62, 0.8)',
                      ],
                      borderColor: [
                        '#3182ce',
                        '#38a169',
                        '#805ad5',
                        '#ed8936',
                        '#e53e3e',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'bottom',
                      labels: {
                        padding: 15,
                        font: {
                          size: 12,
                        },
                        usePointStyle: true,
                        pointStyle: 'circle',
                      },
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 14,
                      },
                      bodyFont: {
                        size: 13,
                      },
                      callbacks: {
                        label: function(context) {
                          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                          const percentage = ((context.parsed / total) * 100).toFixed(1);
                          return context.label + ': ' + context.parsed.toLocaleString() + ' (' + percentage + '%)';
                        }
                      }
                    },
                  },
                }}
                height={300}
              />
            ) : (
              <div className="no-data">No device data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Top Pages and Products */}
      <div className="tables-row">
        {/* Top Pages */}
        <div className="card">
          <div className="card-header">
            <h2>Top Pages</h2>
          </div>
          <div className="card-body">
            {visits && visits.top_pages && visits.top_pages.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Page Path</th>
                    <th>Views</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.top_pages.map((page, index) => {
                    const totalViews = visits.top_pages.reduce((sum, p) => sum + p.views, 0);
                    const percentage = totalViews > 0 ? ((page.views / totalViews) * 100).toFixed(1) : 0;
                    return (
                      <tr key={index}>
                        <td>{page.page_path}</td>
                        <td>{formatNumber(page.views)}</td>
                        <td>{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-data">No page data available</div>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="card">
          <div className="card-header">
            <h2>Top Products</h2>
          </div>
          <div className="card-body">
            {visits && visits.top_products && visits.top_products.length > 0 ? (
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Views</th>
                    <th>Percentage</th>
                  </tr>
                </thead>
                <tbody>
                  {visits.top_products.map((product, index) => {
                    const totalViews = visits.top_products.reduce((sum, p) => sum + p.views, 0);
                    const percentage = totalViews > 0 ? ((product.views / totalViews) * 100).toFixed(1) : 0;
                    return (
                      <tr key={index}>
                        <td>{product.product_name || `Product #${product.product_id}`}</td>
                        <td>{formatNumber(product.views)}</td>
                        <td>{percentage}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            ) : (
              <div className="no-data">No product data available</div>
            )}
          </div>
        </div>
      </div>

      {/* Conversion Funnel */}
      <div className="card">
        <div className="card-header">
          <h2>Conversion Funnel</h2>
        </div>
        <div className="card-body">
          {events && events.conversion_funnel ? (
            <Bar
              data={{
                labels: Object.keys(events.conversion_funnel).map(stage =>
                  stage.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                ),
                datasets: [
                  {
                    label: 'Users',
                    data: Object.values(events.conversion_funnel),
                    backgroundColor: [
                      'rgba(49, 130, 206, 0.8)',
                      'rgba(56, 161, 105, 0.8)',
                      'rgba(128, 90, 213, 0.8)',
                      'rgba(237, 137, 54, 0.8)',
                      'rgba(229, 62, 62, 0.8)',
                    ],
                    borderColor: [
                      '#3182ce',
                      '#38a169',
                      '#805ad5',
                      '#ed8936',
                      '#e53e3e',
                    ],
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                indexAxis: 'y',
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: 'rgba(0, 0, 0, 0.8)',
                    padding: 12,
                    titleFont: {
                      size: 14,
                    },
                    bodyFont: {
                      size: 13,
                    },
                    callbacks: {
                      label: function(context) {
                        const firstValue = Object.values(events.conversion_funnel)[0] || 1;
                        const percentage = ((context.parsed.x / firstValue) * 100).toFixed(1);
                        return 'Users: ' + context.parsed.x.toLocaleString() + ' (' + percentage + '%)';
                      }
                    }
                  },
                },
                scales: {
                  x: {
                    beginAtZero: true,
                    grid: {
                      color: 'rgba(0, 0, 0, 0.05)',
                    },
                    ticks: {
                      font: {
                        size: 11,
                      },
                      callback: function(value) {
                        return value.toLocaleString();
                      }
                    },
                  },
                  y: {
                    grid: {
                      display: false,
                    },
                    ticks: {
                      font: {
                        size: 12,
                      },
                    },
                  },
                },
              }}
              height={300}
            />
          ) : (
            <div className="no-data">No funnel data available</div>
          )}
        </div>
      </div>

      {/* Events Breakdown */}
      <div className="card">
        <div className="card-header">
          <h2>Events by Type</h2>
        </div>
        <div className="card-body">
          {events && events.events_by_type && Object.keys(events.events_by_type).length > 0 ? (
            <div style={{ height: '400px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Doughnut
                data={{
                  labels: Object.keys(events.events_by_type).map(type =>
                    type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                  ),
                  datasets: [
                    {
                      data: Object.values(events.events_by_type),
                      backgroundColor: [
                        'rgba(49, 130, 206, 0.8)',
                        'rgba(56, 161, 105, 0.8)',
                        'rgba(128, 90, 213, 0.8)',
                        'rgba(237, 137, 54, 0.8)',
                        'rgba(229, 62, 62, 0.8)',
                        'rgba(214, 158, 46, 0.8)',
                        'rgba(56, 178, 172, 0.8)',
                      ],
                      borderColor: [
                        '#3182ce',
                        '#38a169',
                        '#805ad5',
                        '#ed8936',
                        '#e53e3e',
                        '#d69e2e',
                        '#38b2ac',
                      ],
                      borderWidth: 2,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: 'right',
                      labels: {
                        padding: 15,
                        font: {
                          size: 12,
                        },
                        usePointStyle: true,
                        pointStyle: 'circle',
                      },
                    },
                    tooltip: {
                      backgroundColor: 'rgba(0, 0, 0, 0.8)',
                      padding: 12,
                      titleFont: {
                        size: 14,
                      },
                      bodyFont: {
                        size: 13,
                      },
                      callbacks: {
                        label: function(context) {
                          const total = context.dataset.data.reduce((sum, val) => sum + val, 0);
                          const percentage = ((context.parsed / total) * 100).toFixed(1);
                          return context.label + ': ' + context.parsed.toLocaleString() + ' (' + percentage + '%)';
                        }
                      }
                    },
                  },
                }}
              />
            </div>
          ) : (
            <div className="no-data">No events data available</div>
          )}
        </div>
      </div>

      {/* Sales and Order Statistics */}
      <div className="stats-row">
        {/* Order Statistics */}
        {orderStats && (
          <div className="card">
            <div className="card-header">
              <h2>Order Statistics</h2>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-label">Total Orders</div>
                  <div className="stat-value">{formatNumber(orderStats.total_orders || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Pending Orders</div>
                  <div className="stat-value">{formatNumber(orderStats.pending_orders || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Completed Orders</div>
                  <div className="stat-value">{formatNumber(orderStats.completed_orders || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Average Order Value</div>
                  <div className="stat-value">{formatCurrency(orderStats.average_order_value || 0)}</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Sales Statistics */}
        {salesStats && (
          <div className="card">
            <div className="card-header">
              <h2>Sales Statistics</h2>
            </div>
            <div className="card-body">
              <div className="stats-grid">
                <div className="stat-item">
                  <div className="stat-label">Total Sales</div>
                  <div className="stat-value">{formatNumber(salesStats.total_sales || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Total Revenue</div>
                  <div className="stat-value">{formatCurrency(salesStats.total_revenue || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Average Sale Value</div>
                  <div className="stat-value">{formatCurrency(salesStats.average_sale_value || 0)}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Top Selling Product</div>
                  <div className="stat-value" style={{ fontSize: '14px' }}>
                    {salesStats.top_product || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Cart Analytics */}
      {cartSummary && (
        <div className="card">
          <div className="card-header">
            <h2>Cart Analytics</h2>
          </div>
          <div className="card-body">
            <div className="cart-stats-grid">
              <div className="cart-stat-card">
                <div className="cart-stat-label">Active Carts</div>
                <div className="cart-stat-value">{formatNumber(cartSummary.active_carts || 0)}</div>
              </div>
              <div className="cart-stat-card">
                <div className="cart-stat-label">Abandoned Carts</div>
                <div className="cart-stat-value">{formatNumber(cartSummary.abandoned_carts || 0)}</div>
              </div>
              <div className="cart-stat-card">
                <div className="cart-stat-label">Checkout Carts</div>
                <div className="cart-stat-value">{formatNumber(cartSummary.checkout_carts || 0)}</div>
              </div>
              <div className="cart-stat-card">
                <div className="cart-stat-label">Abandonment Rate</div>
                <div className="cart-stat-value">{formatPercent(cartSummary.abandonment_rate || 0)}</div>
              </div>
              <div className="cart-stat-card">
                <div className="cart-stat-label">Total Cart Value</div>
                <div className="cart-stat-value">{formatCurrency(cartSummary.total_cart_value || 0)}</div>
              </div>
              <div className="cart-stat-card">
                <div className="cart-stat-label">Avg Cart Value</div>
                <div className="cart-stat-value">{formatCurrency(cartSummary.average_cart_value || 0)}</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Abandoned Carts Table */}
      {abandonedCarts && abandonedCarts.data && abandonedCarts.data.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h2>Recent Abandoned Carts</h2>
          </div>
          <div className="card-body">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Session ID</th>
                  <th>Items</th>
                  <th>Total Value</th>
                  <th>Last Updated</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {abandonedCarts.data.map((cart) => (
                  <tr key={cart.id}>
                    <td>{cart.session_id}</td>
                    <td>{cart.items_count || 0}</td>
                    <td>{formatCurrency(cart.total_value || 0)}</td>
                    <td>{new Date(cart.updated_at).toLocaleString()}</td>
                    <td>
                      <span className="badge badge-warning">{cart.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
